import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { IconChevronDown } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";

export type SortValue = "newest" | "oldest" | "name-asc" | "name-desc";

interface SortDropdownProps {
  value: SortValue;
  onChange: (value: SortValue) => void;
  className?: string;
}

const OPTIONS: { value: SortValue; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name-asc", label: "Name A-Z" },
  { value: "name-desc", label: "Name Z-A" },
];

const LABEL_BY_VALUE = Object.fromEntries(OPTIONS.map((o) => [o.value, o.label])) as Record<
  SortValue,
  string
>;

export function SortDropdown({ value, onChange, className }: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className={clsx("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          "flex items-center gap-3 rounded-xl border border-borderDark bg-bgWhite px-3 py-2 h-[53px]",
          "transition-colors hover:border-brandPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan",
        )}
      >
        <Text variant="button" tone="body" inline>
          {LABEL_BY_VALUE[value]}
        </Text>
        <IconChevronDown
          size={16}

          className={clsx("text-textBody transition-transform", open && "rotate-180")}
        />
      </button>

      {open ? (
        <ul
          role="listbox"
          className="absolute right-0 top-[calc(100%+4px)] z-10 min-w-[160px] rounded-xl border border-borderDefault bg-bgWhite py-1 shadow-lg"
        >
          {OPTIONS.map((option) => {
            const selected = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={clsx(
                    "block w-full px-4 py-2 text-left transition-colors hover:bg-bgLight",
                    selected && "bg-bgLight",
                  )}
                >
                  <Text
                    variant={selected ? "bodySmBold" : "bodySm"}
                    tone="dark"
                    inline
                  >
                    {option.label}
                  </Text>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export function sortValueToQuery(value: SortValue): {
  sortBy: "createdAt" | "name";
  sortOrder: "asc" | "desc";
} {
  switch (value) {
    case "newest":
      return { sortBy: "createdAt", sortOrder: "desc" };
    case "oldest":
      return { sortBy: "createdAt", sortOrder: "asc" };
    case "name-asc":
      return { sortBy: "name", sortOrder: "asc" };
    case "name-desc":
      return { sortBy: "name", sortOrder: "desc" };
  }
}
